import { useAnalytics } from '@newfront-insurance/data-layer-client';
import {
  ChainOfThought,
  ChainOfThoughtConnector,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtStep,
  ChainOfThoughtStepToggle,
  MessageResponse,
} from '@newfront-insurance/nova-core';
import { CheckCircle2Icon } from 'lucide-react';
import type { ReactNode } from 'react';
import { memo, useCallback, useLayoutEffect, useRef } from 'react';

import {
  type CoTToolInputProps,
  CoTToolInput,
  CoTToolOutput,
  CoTToolSearchResults,
} from './chain-of-thought-renderers';
import {
  type CoTStep,
  getCoTStepIcon,
  getCoTStepKey,
  getCoTStepLabel,
  getCoTStepStatus,
  stripLeadingTitle,
} from '../lib/chain-of-thought-utils';

interface ChainOfThoughtBlockProps {
  steps: CoTStep[];
  reasoningIsStreaming: boolean;
  stepProgressMap: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Shared step renderer
// ---------------------------------------------------------------------------

function renderStep(
  step: CoTStep,
  stepIndex: number,
  stepProgressMap: Record<string, string>,
  isLast: boolean,
): ReactNode {
  const stepKey = getCoTStepKey(step, stepIndex);
  const stepStatus = getCoTStepStatus(step);
  const stepProgress = step.type === 'tool' ? stepProgressMap[stepKey] : undefined;
  const inputDescription =
    step.type === 'tool' && step.input != null ? (
      <CoTToolInput {...({ toolName: step.toolName, input: step.input } as CoTToolInputProps)} />
    ) : undefined;
  const description =
    inputDescription && stepProgress ? (
      <>
        {inputDescription}
        <div>{stepProgress}</div>
      </>
    ) : (
      (inputDescription ?? stepProgress)
    );

  return (
    <ChainOfThoughtStep
      key={stepKey}
      icon={getCoTStepIcon(step)}
      label={getCoTStepLabel(step)}
      description={description}
      status={stepStatus}
      isLast={isLast}
    >
      {step.type === 'reasoning' && step.text && (
        <MessageResponse className="text-muted-foreground">
          {step.title ? stripLeadingTitle(step.text) : step.text}
        </MessageResponse>
      )}
      {step.type === 'tool' && stepStatus === 'complete' && step.output != null && (
        <>
          <CoTToolSearchResults toolName={step.toolName} output={step.output} />
          <div className="text-muted-foreground size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
            <CoTToolOutput toolName={step.toolName} output={step.output} />
          </div>
        </>
      )}
    </ChainOfThoughtStep>
  );
}

// ---------------------------------------------------------------------------
// useStableHeight — prevents container from shrinking during streaming
// ---------------------------------------------------------------------------

function useStableHeight(isStreaming: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const maxHeightRef = useRef(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (isStreaming) {
      const height = el.scrollHeight;
      if (height > maxHeightRef.current) {
        maxHeightRef.current = height;
      }
      el.style.minHeight = `${maxHeightRef.current}px`;
    } else {
      el.style.minHeight = '';
      maxHeightRef.current = 0;
    }
  });

  return ref;
}

// ---------------------------------------------------------------------------
// ChainOfThoughtBlock
// ---------------------------------------------------------------------------

export const ChainOfThoughtBlock = memo(
  ({ steps, reasoningIsStreaming, stepProgressMap }: ChainOfThoughtBlockProps) => {
    const stableRef = useStableHeight(reasoningIsStreaming);
    const analytics = useAnalytics();

    const handlePreviousStepsToggle = useCallback(
      (open: boolean) => {
        analytics.track({
          event: 'Chain of Thought Previous Steps Toggled',
          properties: { open, stepCount: steps.length },
        });
      },
      [analytics, steps.length],
    );

    const handleHeaderToggle = useCallback(
      (open: boolean) => {
        analytics.track({
          event: 'Chain of Thought Toggled',
          properties: { open, stepCount: steps.length },
        });
      },
      [analytics, steps.length],
    );

    if (reasoningIsStreaming) {
      const previousSteps = steps.slice(0, -1);
      const hasPreviousSteps = previousSteps.length > 0;

      return (
        <div ref={stableRef}>
          <ChainOfThought isStreaming onOpenChange={handlePreviousStepsToggle}>
            {/* Toggle for previous steps — only when history exists */}
            {hasPreviousSteps && (
              <>
                <ChainOfThoughtStepToggle count={previousSteps.length} />
                <ChainOfThoughtContent>
                  {previousSteps.map((step, i) => renderStep(step, i, stepProgressMap, false))}
                </ChainOfThoughtContent>
              </>
            )}

            {/* Connector between expanded history and current step */}
            {hasPreviousSteps && <ChainOfThoughtConnector />}

            {/* Current step — always visible */}
            {renderStep(steps[steps.length - 1], steps.length - 1, stepProgressMap, true)}
          </ChainOfThought>
        </div>
      );
    }

    // After streaming: standard collapsible with header
    return (
      <ChainOfThought isStreaming={false} onOpenChange={handleHeaderToggle}>
        <ChainOfThoughtHeader>Chain of Thought</ChainOfThoughtHeader>
        <ChainOfThoughtContent>
          {steps.map((step, i) => renderStep(step, i, stepProgressMap, false))}
          <ChainOfThoughtStep key="done" icon={CheckCircle2Icon} label="Done" status="complete" isLast />
        </ChainOfThoughtContent>
      </ChainOfThought>
    );
  },
);

ChainOfThoughtBlock.displayName = 'ChainOfThoughtBlock';
